import { toggleQuery } from "components/scrapEyeDataDashboard/navigation/queryCodecs";
import { useNavigationQuery } from "components/scrapEyeDataDashboard/navigation/NavigationQueryProvider";
import { Stack, Typography } from "@mui/material";
import { EnsureGcsTokenExists } from "components/scrapEyeVideoPlayer/GcsTokenProvider";
import { skipToken } from "@reduxjs/toolkit/query/react";
import { DEFAULT_WINDOW_MINUTES } from "components/manualReview/hooks/useManualReviewNavigation";
import { useTenantData } from "contexts/tenant";
import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import { useTenantTranslation } from "hooks/formatters";
import { useTenant } from "hooks/settings";
import { useMemo } from "react";
import {
  useGetRadioGcsReadTokenQuery,
  useGetSegmentStatusListQuery,
} from "src/store/api/generatedApi";
import { DayWindowTimeline } from "../DayWindowTimeline";
import { useDayWindowNavigation } from "../useDayWindowNavigation";
import { HistoricalRadioContent } from "./HistoricalRadioContent";
import { RadioChannelSelect } from "./RadioChannelSelect";
import { TranscriptScopeToggle } from "./TranscriptScopeToggle";
import {
  useHistoricalRadioUrl,
  useRadioReceivers,
  useRadioSegmentInfo,
} from "./hooks";

dayjs.extend(utc);
dayjs.extend(timezone);

const buildPlantTimezoneLabel = (ianaTimezone: string): string => {
  const abbreviation =
    new Intl.DateTimeFormat("en-US", {
      timeZone: ianaTimezone,
      timeZoneName: "short",
    })
      .formatToParts(new Date())
      .find((part) => part.type === "timeZoneName")?.value ?? ianaTimezone;
  const offsetMinutes = dayjs().tz(ianaTimezone).utcOffset();
  const sign = offsetMinutes >= 0 ? "+" : "−";
  const absMinutes = Math.abs(offsetMinutes);
  const hours = Math.floor(absMinutes / 60);
  const minutes = absMinutes % 60;
  const offset = minutes
    ? `UTC${sign}${hours}:${String(minutes).padStart(2, "0")}`
    : `UTC${sign}${hours}`;
  return `${abbreviation} (${offset})`;
};

const HALF_WINDOW_MINUTES = DEFAULT_WINDOW_MINUTES / 2;
const SECONDS_PER_DAY = 24 * 60 * 60;

type Props = Readonly<{
  deviceId: number;
  deviceName: string;
}>;

export const RadioPanel = ({ deviceId, deviceName }: Props) => {
  const tenantName = useTenant();
  const { t } = useTenantTranslation();
  const { local_timezone } = useTenantData();
  const plantTimezoneLabel = useMemo(
    () => buildPlantTimezoneLabel(local_timezone),
    [local_timezone]
  );

  const { receivers, selectedReceiverId, selectReceiver, channelLabels } =
    useRadioReceivers(deviceId);
  const [allChannels, setAllChannels] = useNavigationQuery(
    "radioAllChannels",
    false,
    toggleQuery,
    "replace"
  );
  const { segmentLengthSec } = useRadioSegmentInfo(deviceId, {
    receiverId: selectedReceiverId,
  });
  const segmentsPerDay = Math.ceil(SECONDS_PER_DAY / segmentLengthSec);

  const nav = useDayWindowNavigation("radioAt", () =>
    dayjs.utc().subtract(HALF_WINDOW_MINUTES, "minute")
  );

  const url = useHistoricalRadioUrl(
    deviceId,
    nav.windowStart.toISOString(),
    nav.windowEnd.toISOString(),
    selectedReceiverId
  );

  const { data: segmentCoverage } = useGetSegmentStatusListQuery(
    deviceName
      ? {
          tenantName,
          deviceName,
          segmentType: "radio",
          start: nav.dayStartIso,
          count: segmentsPerDay,
          segmentLengthSec,
          radioReceiverId: selectedReceiverId,
        }
      : skipToken
  );

  return (
    <EnsureGcsTokenExists useTokenQuery={useGetRadioGcsReadTokenQuery}>
      <Stack p={2} spacing={2}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <RadioChannelSelect
            receivers={receivers}
            selectedReceiverId={selectedReceiverId}
            onSelect={selectReceiver}
          />
          <Typography variant="body2" color="text.secondary">
            {t("radioPlantLocalTime", { tz: plantTimezoneLabel })}
          </Typography>
        </Stack>
        <DayWindowTimeline
          nav={nav}
          segmentCoverageItems={segmentCoverage?.items}
          segmentLengthSec={segmentLengthSec}
          emphasizeSparseSegments
        />
        <TranscriptScopeToggle
          channelCount={receivers.length}
          allChannels={allChannels}
          onChange={setAllChannels}
        />
        <HistoricalRadioContent
          deviceId={deviceId}
          srcUrl={url}
          startTimeIso={nav.windowStart.toISOString()}
          endTimeIso={nav.windowEnd.toISOString()}
          receiverId={selectedReceiverId}
          allChannels={allChannels}
          channelLabels={channelLabels}
          onSelectChannel={selectReceiver}
        />
      </Stack>
    </EnsureGcsTokenExists>
  );
};
